import React from 'react';
import Image from 'next/image';
import { Saira, IBM_Plex_Sans } from 'next/font/google';
import { CarAIBannerMobile } from './CarAIBannerMobile';

const saira = Saira({
  subsets: ['latin'],
  weight: ['700'],
  display: 'swap',
});

const ibmPlexSans = IBM_Plex_Sans({
  subsets: ['latin'],
  weight: ['600'],
  display: 'swap',
});

export interface CarAIBannerProps {
  onBookDemo?: () => void;
}

export const CarAIBanner: React.FC<CarAIBannerProps> = ({ onBookDemo }) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleBookDemo = () => {
    if (onBookDemo) {
      onBookDemo();
    } else {
      // Default behavior - open CarAI demo booking
      window.open('https://carai.com', '_blank');
    }
  };

  // Render mobile version
  if (isMobile) {
    return <CarAIBannerMobile onBookDemo={onBookDemo} />;
  }

  // Render desktop version
  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-r from-[#2D1B69] to-[#4A2B8F] p-[16px] md:py-[24px]">
      <div className="container mx-auto p-[16px]">
        <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-12">
          {/* Left Section - Text Content */}
          <div className="flex flex-col justify-center md:col-span-5">
            <div className="mb-[12px]">
              <div className="mb-[12px]">
                <Image
                  src="/images/caraibanner/logo.svg"
                  alt="CarAI Logo"
                  width={180}
                  height={60}
                  className="h-auto w-[144px] md:w-[176px]"
                  priority
                />
              </div>
              <h2
                className={`${saira.className} mb-[8px] mt-[24px]`}
                style={{
                  color: '#FFF',
                  fontSize: '50px',
                  fontWeight: 700,
                  lineHeight: '62px',
                  maxWidth: '450px',
                }}
              >
                AI Employee for Car Dealerships
              </h2>
            </div>
            <p
              className={ibmPlexSans.className}
              style={{
                color: '#F9BAFF',
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '30px',
                textTransform: 'capitalize',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            >
              Results in 30 days - Or We&apos;ll Give You Your Money Back
            </p>
          </div>

          {/* Middle Section - CTA Button */}
          <div className="flex items-center justify-center md:col-span-2">
            <button
              onClick={handleBookDemo}
              aria-label="Book a Demo"
              className="cursor-pointer transition-transform hover:scale-105"
            >
              <Image
                src="/images/caraibanner/bookademo.svg"
                alt="Book a Demo"
                width={500}
                height={150}
                className="h-auto w-[300px] md:w-[400px]"
                priority
              />
            </button>
          </div>

          {/* Right Section - Images */}
          <div className="relative flex items-center justify-end md:col-span-5">
            <div className="relative h-[220px] w-full overflow-visible md:h-[280px]">
              {/* Laptop Image */}
              <div className="absolute right-0 top-0 z-10 w-[120%] md:w-[95%]">
                <Image
                  src="/images/caraibanner/laptop.png"
                  alt="CarAI Dashboard on Laptop"
                  width={800}
                  height={500}
                  className="h-auto w-full drop-shadow-2xl"
                  priority
                />
              </div>

              {/* Phone Image */}
              <div className="absolute left-[0px] top-[8%] z-20 w-[28%] md:left-[0px] md:w-[25%]">
                <Image
                  src="/images/caraibanner/phone.png"
                  alt="CarAI App on Phone"
                  width={300}
                  height={600}
                  className="h-auto w-full drop-shadow-2xl"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative dotted line */}
      <div className="absolute bottom-0 left-0 right-0 border-b-2 border-dotted border-white opacity-30"></div>
    </section>
  );
};

export default CarAIBanner;
